import React, { useState, useEffect, useRef } from 'react';
import { Star, Quote } from 'lucide-react';
import { mockData } from '../mock';

// Custom hook for scroll animation
const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(element);
    return () => element && observer.unobserve(element);
  }, [threshold]);

  return [ref, isVisible];
};

const TestimonialsSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.2);
  const [videoRef, videoVisible] = useScrollAnimation(0.1);
  const [cardsRef, cardsVisible] = useScrollAnimation(0.1);

  return (
    <section id="testimonials" style={{ padding: '5rem 1.5rem', background: 'var(--bg-page)' }}>
      <div className="container">
        {/* Animated Header */}
        <div 
          ref={headerRef}
          className={`fade-up ${headerVisible ? 'visible' : ''}`}
          style={{ textAlign: 'center', marginBottom: '3rem' }}
        >
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            Student Success Stories
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            See what our students say about their experience with Abba Academy
          </p>
        </div>

        {/* Animated YouTube Video Section */}
        <div 
          ref={videoRef}
          className={`scale-up ${videoVisible ? 'visible' : ''}`}
          style={{ maxWidth: '900px', margin: '0 auto 4rem' }}
        >
          <div className="product-card" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src="https://www.youtube.com/embed/QBAZSRZJQZI"
                title="Abba Academy Student Testimonials"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
              ></iframe>
            </div>
            <div style={{ padding: '1.5rem', textAlign: 'center' }}>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>Watch Our Students' Success Stories</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                Hear directly from students about their journey with Dr. Michael Omakobia
              </p>
            </div>
          </div>
        </div>

        {/* Animated Written Testimonials Grid */}
        <div ref={cardsRef} className="ai-grid">
          {mockData.testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.id} 
              className={`product-card ${testimonial.isNew ? 'glow-on-visible' : ''}`}
              style={{
                opacity: cardsVisible ? 1 : 0,
                transform: cardsVisible ? 'translateY(0)' : 'translateY(30px)',
                transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                transitionDelay: `${index * 0.1}s`,
                border: testimonial.isNew ? '2px solid #C5A059' : undefined
              }}
            >
              {/* NEW Badge for Anja */}
              {testimonial.isNew && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '10px',
                  background: '#C5A059',
                  color: '#0F172A',
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase'
                }}>
                  Latest
                </div>
              )}
              <div style={{ marginBottom: '1rem' }}>
                <Quote size={32} color="var(--accent-primary)" />
              </div>
              
              {/* Star Rating */}
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
                {[...Array(5)].map((_, index) => (
                  <Star key={index} size={16} fill="var(--accent-primary)" color="var(--accent-primary)" />
                ))}
              </div>
              
              <p className="body-medium" style={{ color: 'var(--text-body)', marginBottom: '1.5rem', fontStyle: 'italic' }}>
                "{testimonial.comment}"
              </p>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border-light)' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'var(--accent-wash)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  color: 'var(--accent-text)',
                  fontSize: '0.875rem'
                }}>
                  {testimonial.avatar}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                    {testimonial.name}
                  </div>
                  <div className="caption">
                    {testimonial.grade}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
